import { PROPERTY_STATUS } from "../../utils/constants";
import { isTemporaryUnit, normalizeUnitId } from "../../utils/property.util";

const MEMBER_FIELDS = [
  "readyForMarketing",
  "isAvailable",
  "effectiveFromDate",
  "supplementalPortfolioName",
  "leaseInfo",
  "unitAreaSF",
  "initialPublishDate",
  "lastPublishDate",
  "lastUnpublishDate",
  "createdAt",
];

export const removeTemporaryUnitMarketingFields = unit => {
  if (!isTemporaryUnit(unit)) return unit;
  const result = { ...unit, isTemp: true };
  delete result.readyForMarketing;
  delete result.effectiveFromDate;
  delete result.isAvailable;
  delete result.supplementalPortfolioName;
  return result;
};

// The editor has one row for a group, but publication and eligibility belong to its members.
export const applyMemberPublication = (unit, savedUnits = [], action = "save", isDraft, rfmEnabled = false) => {
  const saved = savedUnits.find(member => normalizeUnitId(member.unitId) === normalizeUnitId(unit.unitId));
  if (!saved || (!unit.ghostUnitCode && !saved.ghostUnitCode)) return unit;
  const result = { ...unit };
  MEMBER_FIELDS.forEach(field => {
    if (Object.prototype.hasOwnProperty.call(saved, field)) result[field] = saved[field];
    else delete result[field];
  });
  const isIneligible =
    String(saved.readyForMarketing || "")
      .trim()
      .toLowerCase() !== "yes" || saved.isAvailable === false;
  if (action === "save" || (action === "publish" && rfmEnabled && isIneligible && !unit.isTemp && !saved.isTemp)) {
    result.status = saved.status;
    result.isDraft = isDraft === true ? true : saved.isDraft;
  }
  return result;
};

// Published rows are preselected by the form. That selection is not a new publish request.
export const getPropertyPublicationAction = (unit, savedUnits = [], selectedForPublishing = false) => {
  if (!selectedForPublishing) return "save";
  const previousMembers = unit.ghostUnitCode
    ? savedUnits.filter(member => member.ghostUnitCode === unit.ghostUnitCode)
    : savedUnits.filter(member => normalizeUnitId(member.unitId) === normalizeUnitId(unit.unitId));
  const wasPublished = previousMembers.some(member => member.status === PROPERTY_STATUS.published && !member.isDraft);
  return wasPublished ? "save" : "publish";
};

// Hide inactive members only in the published group display; never remove their form values.
export const isMergedMemberVisible = (unitId, group, savedUnits = []) => {
  if (!group.ghostUnitCode || group.status !== PROPERTY_STATUS.published) return true;
  const member = savedUnits.find(
    unit => normalizeUnitId(unit.unitId) === normalizeUnitId(unitId) && unit.ghostUnitCode === group.ghostUnitCode,
  );
  return !member || member.status !== PROPERTY_STATUS.unpublished;
};

export const canPublishHiddenMergedMembers = (group, savedUnits = [], rfmEnabled = true) => {
  if (!group.ghostUnitCode || group.status !== PROPERTY_STATUS.published) return false;
  return savedUnits.some(
    member =>
      member.ghostUnitCode === group.ghostUnitCode &&
      member.status === PROPERTY_STATUS.unpublished &&
      (!rfmEnabled ||
        isTemporaryUnit(member) ||
        (String(member.readyForMarketing || "")
          .trim()
          .toLowerCase() === "yes" &&
          member.isAvailable === true)),
  );
};

// Removing a rendered tag must not implicitly delete a hidden member (including Backspace).
export const preserveHiddenMergedMembers = (nextCodes, currentCodes = [], group, savedUnits = []) => {
  const selectedIds = new Set(nextCodes.map(code => normalizeUnitId(code.value)));
  const hiddenCodes = currentCodes.filter(
    code => !isMergedMemberVisible(code.value, group, savedUnits) && !selectedIds.has(normalizeUnitId(code.value)),
  );
  return [...nextCodes, ...hiddenCodes];
};

export const getMergedPublication = (unit, members = []) => {
  if (!unit.ghostUnitCode) return { status: unit.status, isDraft: unit.isDraft };
  const group = members.filter(member => member.ghostUnitCode === unit.ghostUnitCode);
  const published = group.filter(member => member.status === PROPERTY_STATUS.published);
  if (published.length) {
    return { status: PROPERTY_STATUS.published, isDraft: published.every(member => member.isDraft === true) };
  }
  return {
    status: group.some(member => member.status === PROPERTY_STATUS.unpublished)
      ? PROPERTY_STATUS.unpublished
      : PROPERTY_STATUS.never_published,
    isDraft: group.every(member => member.isDraft === true),
  };
};
